import {
  postLogin,
  postPasswordRegistration,
  type LoginStatus,
  type PasswordRegistrationStatus
} from 'shared/api/authApi'
import { clearLegacyCognitoCookies } from 'shared/auth/legacyCognitoCookies'
import { getRecaptchaToken } from 'shared/auth/recaptcha'
import { getApiErrorMessage, getApiErrorStatus } from 'shared/utils/apiError'
import { t } from 'shared/i18n'

/**
 * 認証の途中状態が失効し、ログインからやり直す必要があることを表すエラー。
 * 途中状態は api の HttpOnly Cookie で運ばれるため、クライアント側で復旧する手段がない。
 */
export class LoginRestartRequiredError extends Error {
  constructor (message: string) {
    super(message)
    this.name = 'LoginRestartRequiredError'
  }
}

/** 想定外の失敗を「ログイン中にエラーが発生しました: ...」の形へ写す。 */
const unexpectedLoginError = (error: unknown): Error => {
  const detail = getApiErrorMessage(error) ?? (error instanceof Error ? error.message : null)
  return new Error(t('shared.login.text6', { value: detail ?? 'Unknown error' }))
}

/**
 * メールアドレスとパスワードでログインを試みる（`POST /v1/auth/login`）。
 * React state には直接触れない。
 *
 * - 401: 資格情報の不一致（ユーザー不存在と同じ外形）。従来の Cognito
 *   `NotAuthorizedException` と同じ文言を出す
 * - 403 / 422 / 429: reCAPTCHA 不合格（`recaptcha_failed`）・トークン欠落（`recaptcha_token_missing`）
 *   などのリクエスト不正・試行回数超過。api が日本語の `detail` を用意しているのでそのまま出す。
 *   再送時は新しい reCAPTCHA トークンを取り直す
 *
 * @returns ログイン後の状態（`authenticated` / `mfaRequired` / `passwordRegistrationRequired`）
 * @throws 日本語化したエラー
 */
export async function performLoginAttempt (email: string, password: string): Promise<LoginStatus> {
  // 切替前の Cognito ログインの Cookie が残っていると、api は legacy 経路でそのユーザーとして
  // 認証し続ける。別ユーザーでログインし直すときに取り違えないよう先に消す
  // （Amplify 時代の「サインイン済みなら signOut してから signIn」に相当）。
  clearLegacyCognitoCookies()

  // reCAPTCHA トークンは送信の直前に取る（2 分で失効し 1 回しか使えないため取り置かない）。
  const recaptchaToken = await getRecaptchaToken()

  try {
    return await postLogin({ email, password, recaptchaToken })
  } catch (error: unknown) {
    const status = getApiErrorStatus(error)
    if (status === 401) {
      throw new Error(t('shared.login.text3'))
    }
    const detail = getApiErrorMessage(error)
    if ((status === 403 || status === 422 || status === 429) && detail != null) {
      throw new Error(detail)
    }
    throw unexpectedLoginError(error)
  }
}

/**
 * 仮パスワードで招待されたユーザーの初回パスワードを登録する
 * （`POST /v1/auth/password_registrations`）。
 *
 * - 422: パスワードポリシー不適合。従来の `InvalidPasswordException` と同じ文言を出す
 * - 401: 途中状態（15 分）の期限切れ・再利用。ログインからやり直させる
 *
 * @returns 登録後の状態（`authenticated` / `mfaRequired`）
 */
export async function completePasswordRegistration (newPassword: string): Promise<PasswordRegistrationStatus> {
  if (!newPassword) {
    throw new Error(t('shared.login.error'))
  }

  try {
    return await postPasswordRegistration(newPassword)
  } catch (error: unknown) {
    const status = getApiErrorStatus(error)
    if (status === 422) {
      throw new Error(t('shared.login.text'))
    }
    if (status === 401) {
      throw new LoginRestartRequiredError(t('shared.login.expired'))
    }
    const detail = getApiErrorMessage(error) ?? (error instanceof Error ? error.message : null)
    throw new Error(t('shared.login.text7', { value: detail ?? 'Unknown error' }))
  }
}
